// Copied verbatim into three repos:
//   studx-admin-frontend/vuejs/src/utils/video-source.js
//   studx-student-frontend/vuejs/src/utils/video-source.js
//   studx-teacher-frontend/vuejs/src/utils/video-source.js
// tests/unit/video-source.spec.js travels with it; `diff` between any two
// copies of either file must produce no output.
//
// This module used to build the playback URL in the browser, from
// VUE_APP_BUNNY_CDN_HOSTNAME_* for Bunny and, for Wowza, config.streamServer plus
// the signed-in user's e_code/r_stage. config.streamServer is not the same env var
// in all three repos: admin and teacher (vuejs/src/utils/upload.js:4) bind it to
// VUE_APP_VDO_STREAMING_SERVER, while student (vuejs/src/utils/upload.js:2) binds it
// to VUE_APP_STREAMING_SERVER. It cannot build either URL any more: Bunny URLs are
// now signed with a CDN token authentication key, and that key must never reach
// a browser bundle. The server resolves both providers in resolvePlayback
// (studx-admin-backend/src/utils/playback.js) and sends the finished URL as
// playbackUrl, so the whole build step -- and with it the last copy of CDN
// knowledge on the client -- is gone.

const HLS_MIME = 'application/x-mpegURL';

/**
 * The video.js source for a VdoSetTopic, whichever backend holds its video.
 *
 * @param {object|null|undefined} topic a VdoSetTopic as loaded over GraphQL
 * @return {{src: string, type: string}|null} null when there is nothing
 *   playable yet -- call sites render an empty state instead of mounting a
 *   player
 * @throws {Error} when the topic was loaded by a query that did not select
 *   playbackStatus
 */
export function resolveVideoSource(topic) {
  if (!topic) return null;

  if (topic.playbackStatus === undefined) {
    // Not a data state: playbackStatus is non-null in the schema, so every
    // topic has one. undefined means the GraphQL document that loaded this
    // topic omitted the field, and returning null here would render an empty
    // player with no clue why. scripts/check-video-fields.js is the build-time
    // gate; this throw is the backstop for anything it cannot see.
    throw new Error(
      `resolveVideoSource: playbackStatus is missing on topic ${topic.id}. ` +
      'Add playbackUrl and playbackStatus to the query that loaded it, then ' +
      'run: node scripts/check-video-fields.js'
    );
  }

  if (topic.playbackStatus !== 'READY') return null;

  // READY with a blank URL is a server-side contradiction rather than a state
  // worth rendering: video.js on an empty src shows a spinner forever.
  const src = (topic.playbackUrl || '').trim();
  if (!src) return null;

  return { src, type: HLS_MIME };
}

/**
 * Whether a VdoSetTopic holds a video at all, from either provider.
 *
 * This is the counting rule behind every "จำนวนวิดีโอ / vdo qty" figure. It is
 * deliberately NOT resolveVideoSource: those figures answer "how many videos
 * does this unit hold", not "how many can play this second", so a Bunny video
 * that is still encoding counts, and a query that did not select
 * playbackStatus can still call this (resolveVideoSource throws on that).
 *
 * Written because every counter used to read `topic.vdoStreamPath` alone --
 * the Wowza column, which a Bunny upload never writes (the backend's
 * createBunnyUpload sets videoProvider/bunnyVideoGuid and nothing else). Units
 * full of Bunny videos reported 0 videos while showing a real totalVdoLength
 * and a working play button next to every topic.
 *
 * Reads bunnyVideoGuid rather than videoProvider for the same reason
 * onDownloadClick and onDeleteVideoClick do: videoProvider stays 'bunny' after
 * a Bunny video is deleted, so a topic with no video at all would still count.
 *
 * @param {object|null|undefined} topic a VdoSetTopic as loaded over GraphQL
 * @return {boolean}
 */
export function hasVideo(topic) {
  return !!(topic && (topic.vdoStreamPath || topic.bunnyVideoGuid));
}
